<template>
  <transition name="fade">
    <div v-if="isShowing">
      <!-- Success Alert Modal -->
      <div :id="modalType" class="modal fade show" tabindex="-1" role="dialog" aria-modal="true">
        <div class="modal-dialog modal-dialog-centered" :class="modalStyle">
          <div class="modal-content" :class="backgroundColor">
            <div
              v-if="withHeader"
              class="modal-header py-1 text-white d-flex justify-content-center"
              :class="headerClass"
            >
              <h3 class="my-2 mx-auto font-semibold lh-34">
                <i v-if="showHeaderIcon && withHeader" :class="modalIconType"></i> {{ title }}
              </h3>
              <base-button
                type="button"
                v-show="close"
                item-class="close m-auto text-white border-0"
                @click="$emit('close')"
                ><i class="mdi mdi-close"></i>
              </base-button>
            </div>
            <base-button
              type="button"
              v-show="close && !withHeader"
              item-class="close p-2 border-0"
              @click="$emit('close')"
              ><i class="mdi mdi-close"></i>
            </base-button>
            <div class="modal-body" :class="modalBodyClass">
              <div class="text-center">
                <i v-if="!withHeader && withIcon" class="h1" :class="modalIconType"></i>
                <slot name="header"></slot>
                <slot name="body"></slot>
                <slot name="footer"></slot>
              </div>
            </div>
          </div>
          <!-- /.modal-content -->
        </div>
        <!-- /.modal-dialog -->
      </div>
      <!-- /.modal -->
      <div class="modal-backdrop fade show"></div>
    </div>
  </transition>
</template>

<script setup>
import { watch, computed } from 'vue'

// #region - importing components
import BaseButton from '@/components/BaseButton.vue'
// endregion

// #region - Props
const props = defineProps({
  title: {
    type: String,
    default: ''
  },
  type: {
    type: String,
    default: 'success'
  },
  isShowing: {
    type: Boolean,
    default: false
  },
  size: {
    type: String,
    default: 'sm'
  },
  close: {
    type: Boolean,
    default: true
  },
  scrollable: {
    type: Boolean,
    default: false
  },
  withHeader: {
    type: Boolean,
    default: false
  },
  withIcon: {
    type: Boolean,
    default: true
  },
  modalBodyClass: {
    type: String,
    default: 'p-4'
  },
  showHeaderIcon: {
    type: Boolean,
    default: true
  },
  severity: {
    type: Number,
    default: 0
  }
})
// endregion

// #region - Emits
const emit = defineEmits(['close'])
// endregion

// #region - computed properties
const modalType = computed(() => {
  return `${props.type}-alert-modal`
})

const modalSize = computed(() => {
  return `modal-${props.size}`
})

const modalStyle = computed(() => {
  if (props.scrollable) {
    return modalSize.value + ` modal-dialog-scrollable`
  } else {
    return modalSize.value
  }
})

const backgroundColor = computed(() => ``)

const modalIconType = computed(() => {
  switch (props.type) {
    case 'warning':
      return 'mdi mdi-alert-outline mdi-40px text-warning'
    case 'danger':
      return 'mdi mdi-cancel mdi-40px text-danger'
    case 'info':
      return 'mdi mdi-information-outline mdi-40px text-info'
    case 'success':
      if (props.severity === 3) {
        return 'mdi mdi-party-popper'
      }
      break
    default:
      return 'mdi mdi-check-bold mdi-40px text-success'
  }
})

const headerClass = computed(() => {
  switch (props.type) {
    case 'warning':
      return 'bg-warning'
    case 'danger':
      return 'bg-danger'
    case 'info':
      return 'bg-info'
    case 'success':
      if (props.severity === 3) {
        return 'bg-success'
      }
      break
    default:
      return ''
  }
})
// endregion

// #region - Method definitions
const toggleBodyClass = (method, bodyClass) => {
  const el = document.body

  if (method === 'add') {
    el.classList.add(bodyClass)
  } else {
    el.classList.remove(bodyClass)
  }
}
// endregion

// #region - watch part
watch(
  () => props.isShowing,
  newValue => {
    if (newValue === true) {
      toggleBodyClass('add', 'modal-open')
    } else {
      toggleBodyClass('remove', 'modal-open')
    }
  }
)
// endregion
</script>

<style lang="scss" scoped>
.modal {
  &.show {
    padding-right: 15px;
    display: block;
  }
  .close {
    position: absolute;
    right: 0;
    z-index: 9;
  }
  .lh-34 {
    line-height: 34px;
  }

  &-content {
    border-top-left-radius: 0.5rem;
    border-top-right-radius: 0.5rem;
  }
}
</style>
